import React, { ChangeEvent, MouseEvent } from "react";
import {
    Button,
    createStyles,
    Grid,
    IconButton,
    InputAdornment,
    Paper,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Theme,
    Typography,
    withStyles,
    WithStyles
} from "@material-ui/core";
import {
    AddCircleOutline,
    Autorenew,
    CloudDownload,
    Edit,
    Search
} from "@material-ui/icons";
import saveAs from "file-saver";
import $ from "jquery";
import { connect } from "react-redux";
import { Link, LinkProps } from "react-router-dom";
import { bindActionCreators } from "redux";
import FileApi from "../../../api/FileApi";
import { formatAuctionDateTime } from "../../../helpers/auctionDateTime";
import IUserInfo from "../../../models/account/IUserInfo";
import { LoadState } from "../../../models/utility/LoadState";
import { IAppDispatch, IAppState } from "../../../store";
import * as AuctionStore from "../../../store/AuctionStore";
import * as UserStore from "../../../store/UserStore";
import ResetTermsAcceptanceDialog from "../../dialogs/ResetTermsAcceptanceDialog";
import UpdateUserStatusConfirmationDialog from "../../dialogs/UpdateUserStatusConfirmationDialog";
import FullscreenLoader from "../../layout/FullscreenLoader";
import SectionBanner from "../../shared/SectionBanner";
import Constants from "./../../../models/utility/Constants";

type ManageUserListProps = UserStore.IUserStoreState &
    typeof UserStore.actionCreators &
    WithStyles<typeof styles>;

interface IManageUserListState {
    bidders: IUserInfo[];
    filter: string;
    page: number;
    rowsPerPage: number;
    showUpdateUserStatusConfirmationDialog: boolean;
    updateUserStatusEmail: string;
    updateUserStatusEnable: boolean;
    showResetTermsAcceptanceDialog: boolean;
}
const WhiteIconButton = withStyles({
    root: {
        color: "#000"
    }
})(IconButton) as typeof IconButton;
const WhiteButton = withStyles({
    root: {
        backgroundColor: "#000",
        color: "rgba(248, 179, 50, 1)"
    }
})(Button);

const styles = (theme: Theme) =>
    createStyles({
        buttonCreateNew: { float: "right" },
        editButton: { padding: theme.spacing() },
        exportButtonDivider: {
            width: theme.spacing(2)
        },
        exportButtonWrapper: {
            display: "flex",
            alignItems: "center"
        },
        paper: {
            alignItems: "center",
            display: "flex",
            flexDirection: "column"
        },
        searchContainer: { textAlign: "right" },
        searchInput: {
            fontSize: 8,
            backgroundColor: "#000"
        },
        section: {
            [theme.breakpoints.down("xs")]: {
                paddingLeft: theme.bodyContent.mobilePadding,
                paddingRight: theme.bodyContent.mobilePadding
            },
            backgroundColor: theme.palette.background.paper,
            paddingLeft: theme.bodyContent.desktopPadding,
            paddingRight: theme.bodyContent.desktopPadding,
            position: "relative",
            width: "100%"
        },
        sectionContent: {
            [theme.breakpoints.down("xs")]: {
                paddingBottom: theme.bodyContent.mobilePadding,
                paddingTop: theme.bodyContent.mobilePadding
            },
            margin: "0 auto",
            maxWidth: theme.bodyContent.maxWidth,
            paddingBottom: theme.bodyContent.desktopPadding,
            paddingTop: theme.bodyContent.desktopPadding,
            width: "100%"
        },
        table: {
            marginBottom: theme.spacing(5),
            width: "100%"
        },
        tableCell: {
            borderColor: theme.palette.divider,
            minWidth: "130px" // Keeps the `Phone` cell from wrapping
        },
        tableCellButton: {
            borderColor: theme.palette.divider,
            paddingLeft: theme.spacing(),
            paddingRight: 0
        },
        tableCellHeader: { borderColor: "transparent" },
        tableHeader: { fontWeight: "bold" },
        tableLast: { marginBottom: 0 },
        tableRow: {
            "&:nth-of-type(odd)": {
                backgroundColor: theme.palette.background.default
            },
            borderColor: theme.palette.grey[500]
        },
        tableFooter: {
            marginBottom: theme.spacing(4)
        },
        bidderListButtonWrapper: {
            display: "flex",
            gap: theme.spacing(1.5),
            padding: theme.spacing(1)
        }
    });

class ManageUserList extends React.PureComponent<
    ManageUserListProps,
    IManageUserListState
> {
    constructor(props: ManageUserListProps) {
        super(props);
        Constants.SetDocumentTitle("Admin - Manage Users");
        this.state = {
            bidders: [],
            filter: "",
            page: 0,
            rowsPerPage: 20,
            showUpdateUserStatusConfirmationDialog: false,
            updateUserStatusEmail: "",
            updateUserStatusEnable: false,
            showResetTermsAcceptanceDialog: false
        };
    }

    public componentWillMount() {
        $("body, html").animate(
            { scrollTop: 0 },
            Constants.ScrollAnimationLength
        );
        this.props.loadUserList();
    }

    public render() {
        if (this.props.usersLoadState !== LoadState.Loaded) {
            return <FullscreenLoader />;
        }

        const classes = this.props.classes;
        const admins = this.props.admins;
        const bidders = this.state.filter
            ? this.state.bidders
            : this.props.bidders;

        const sortNameByLastAndFirst = (a: IUserInfo, b: IUserInfo) => {
            if (a.lastName < b.lastName) return -1;
            if (a.lastName > b.lastName) return 1;
            if (a.firstName < b.firstName) return -1;
            if (a.firstName > b.firstName) return 1;
            return 0;
        };

        return (
            <Paper className={classes.paper} elevation={2}>
                <SectionBanner leftSideText="Admin - Manage Users" />
                <UpdateUserStatusConfirmationDialog
                    visible={this.state.showUpdateUserStatusConfirmationDialog}
                    closeDialog={this.closeUpdateUserStatusConfirmationDialog}
                    updateUserStatus={this.updateUserStatus(
                        this.state.updateUserStatusEmail,
                        this.state.updateUserStatusEnable
                    )}
                    updateUserStatusEmail={this.state.updateUserStatusEmail}
                    updateUserStatusEnable={this.state.updateUserStatusEnable}
                />
                <div className={classes.section}>
                    <div className={classes.sectionContent}>
                        {/* Bidders */}
                        <Paper>
                            <Table
                                className={`${classes.table} ${classes.tableLast}`}
                            >
                                <TableHead>
                                    <TableRow>
                                        <TableCell
                                            className={classes.tableCellHeader}
                                            colSpan={6}
                                        >
                                            <Typography
                                                className={classes.tableHeader}
                                                variant="subtitle1"
                                            >
                                                Bidders
                                            </Typography>
                                        </TableCell>
                                        <TableCell
                                            colSpan={2}
                                            className={`${classes.tableCellHeader} ${classes.searchContainer}`}
                                        >
                                            <TextField
                                                className={classes.searchInput}
                                                label="search user..."
                                                value={this.state.filter}
                                                onChange={this.onFilterChanged}
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <Search
                                                                viewBox="0 0 25 25"
                                                                color="primary"
                                                            />
                                                        </InputAdornment>
                                                    )
                                                }}
                                            />
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell
                                            className={classes.tableCellButton}
                                        />
                                        <TableCell
                                            className={classes.tableCellButton}
                                        >
                                            Enabled
                                        </TableCell>
                                        <TableCell
                                            className={classes.tableCell}
                                        >
                                            Name
                                        </TableCell>
                                        <TableCell
                                            className={classes.tableCell}
                                        >
                                            Email
                                        </TableCell>
                                        <TableCell
                                            className={classes.tableCell}
                                        >
                                            Phone
                                        </TableCell>
                                        <TableCell
                                            className={classes.tableCell}
                                        >
                                            City
                                        </TableCell>
                                        <TableCell
                                            className={classes.tableCell}
                                        >
                                            Province
                                        </TableCell>
                                        <TableCell
                                            className={classes.tableCell}
                                        >
                                            Registered On
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {bidders
                                        .slice(
                                            this.state.page *
                                                this.state.rowsPerPage,
                                            this.state.page *
                                                this.state.rowsPerPage +
                                                this.state.rowsPerPage
                                        )
                                        .map((user) =>
                                            this.renderUserRow(user, false)
                                        )}
                                </TableBody>
                            </Table>
                            <Grid
                                container={true}
                                className={classes.tableFooter}
                            >
                                <Grid
                                    item={true}
                                    xs={4}
                                    className={classes.bidderListButtonWrapper}
                                >
                                    <WhiteButton
                                        size="small"
                                        variant="outlined"
                                        color="primary"
                                        onClick={this.downloadFile}
                                    >
                                        <CloudDownload />
                                        &nbsp; Download
                                    </WhiteButton>
                                    <WhiteButton
                                        size="small"
                                        variant="outlined"
                                        color="primary"
                                        onClick={
                                            this.showResetTermsAcceptanceDialog
                                        }
                                    >
                                        <Autorenew />
                                        &nbsp; Reset Terms
                                    </WhiteButton>
                                </Grid>

                                <Grid item={true} xs={8}>
                                    <TablePagination
                                        rowsPerPageOptions={[5, 10, 20, 50]}
                                        component="div"
                                        count={bidders.length}
                                        rowsPerPage={this.state.rowsPerPage}
                                        page={this.state.page}
                                        backIconButtonProps={{
                                            "aria-label": "Previous Page"
                                        }}
                                        nextIconButtonProps={{
                                            "aria-label": "Next Page"
                                        }}
                                        onPageChange={this.handleChangePage}
                                        onRowsPerPageChange={
                                            this.handleChangeRowsPerPage
                                        }
                                    />
                                </Grid>
                            </Grid>
                        </Paper>

                        {/* Admins */}
                        <Paper>
                            <Table className={classes.table}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell
                                            className={classes.tableCellHeader}
                                            colSpan={4}
                                        >
                                            <Typography
                                                className={classes.tableHeader}
                                                variant="subtitle1"
                                            >
                                                Administrators
                                            </Typography>
                                        </TableCell>
                                        <TableCell
                                            className={classes.tableCellHeader}
                                            colSpan={2}
                                        >
                                            <Button
                                                className={
                                                    classes.buttonCreateNew
                                                }
                                                size="small"
                                                variant="outlined"
                                                color="default"
                                                component={
                                                    this.auctionCreateLink
                                                }
                                            >
                                                <AddCircleOutline />
                                                &nbsp;Create New
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell
                                            className={classes.tableCellButton}
                                        />
                                        <TableCell
                                            className={classes.tableCell}
                                            colSpan={2}
                                        >
                                            Name
                                        </TableCell>

                                        <TableCell
                                            className={classes.tableCell}
                                        >
                                            Email
                                        </TableCell>
                                        <TableCell
                                            className={classes.tableCell}
                                        >
                                            Phone
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {[...admins]
                                        .sort(sortNameByLastAndFirst)
                                        .map((user) =>
                                            this.renderUserRow(user, true)
                                        )}
                                </TableBody>
                            </Table>
                        </Paper>
                    </div>
                </div>
                <ResetTermsAcceptanceDialog
                    visible={this.state.showResetTermsAcceptanceDialog}
                    closeDialog={this.closeResetTermsAcceptanceDialog}
                />
            </Paper>
        );
    }

    public updateUserStatus = (email: string, enable: boolean) => () => {
        this.props.updateUserStatus(email, enable);
        this.closeUpdateUserStatusConfirmationDialog();
    };

    public closeUpdateUserStatusConfirmationDialog = () => {
        this.setState({ showUpdateUserStatusConfirmationDialog: false });
    };

    private showUpdateUserStatusConfirmationDialog = () => {
        this.setState({ showUpdateUserStatusConfirmationDialog: true });
    };

    private handleUserStatusChange =
        (email: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
            this.showUpdateUserStatusConfirmationDialog();
            this.setState({
                updateUserStatusEmail: email,
                updateUserStatusEnable: event.target.checked
            });
        };

    private downloadFile = async () => {
        const result = await FileApi.GetBiddersCsvFile();
        saveAs(result.blob, result.fileName);
    };

    private onFilterChanged = (event: ChangeEvent<HTMLInputElement>) => {
        const filter = event.target.value.trim();
        const bidders = this.filterData(filter);
        const page =
            this.state.page < bidders.length / this.state.rowsPerPage
                ? this.state.page
                : 0;
        this.setState({ bidders, filter, page });
    };

    private handleChangePage = (
        event: MouseEvent<HTMLButtonElement> | null,
        page: number
    ) => {
        this.setState({ page });
    };

    private handleChangeRowsPerPage = (
        event: ChangeEvent<HTMLInputElement>
    ) => {
        const rowsPerPage = +event.target.value;
        this.setState({ rowsPerPage });
    };

    private filterData = (searchFilter: string) => {
        const bidders = this.props.bidders;
        if (!searchFilter || searchFilter.length === 0) {
            return [];
        }

        return bidders.filter((elem) => {
            return (
                `${elem.city} ${elem.email} ${elem.firstName} ${elem.lastName} ${elem.phoneNumber} ${elem.province}`.search(
                    new RegExp(
                        searchFilter.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
                        "i"
                    )
                ) >= 0
            );
        });
    };

    private renderUserRow = (user: IUserInfo, isAdmin: boolean) => {
        const classes = this.props.classes;
        const switchButton = (
            <TableCell className={classes.tableCellButton}>
                <Switch
                    checked={user.enabled}
                    value="a"
                    color="primary"
                    onChange={this.handleUserStatusChange(user.email)}
                />
            </TableCell>
        );

        return (
            <TableRow key={user.email} className={classes.tableRow}>
                <TableCell className={classes.tableCellButton}>
                    <WhiteIconButton
                        className={classes.editButton}
                        component={this.userDetailLink(user)}
                    >
                        <Edit fontSize="small" />
                    </WhiteIconButton>
                </TableCell>
                {!isAdmin ? switchButton : null}

                <TableCell
                    className={classes.tableCell}
                    colSpan={isAdmin ? 2 : 1}
                >
                    {`${user.firstName} ${user.lastName}`}
                </TableCell>
                <TableCell className={classes.tableCell}>
                    {user.email}
                </TableCell>
                <TableCell className={classes.tableCell}>
                    {user.phoneNumber}
                </TableCell>
                {!isAdmin && (
                    <TableCell className={classes.tableCell}>
                        {user.city}
                    </TableCell>
                )}
                {!isAdmin && (
                    <TableCell className={classes.tableCell}>
                        {user.province}
                    </TableCell>
                )}
                {!isAdmin && (
                    <TableCell className={classes.tableCell}>
                        {formatAuctionDateTime(user.registrationDate)?.isoDate}
                    </TableCell>
                )}
            </TableRow>
        );
    };

    private userDetailLink =
        (user: IUserInfo) => (props: Partial<LinkProps>) => (
            <Link to={Constants.Routes.AdminUserDetail(user.id)} {...props} />
        );
    private auctionCreateLink = (props: Partial<LinkProps>) => (
        <Link
            to={Constants.Routes.AdminUserDetail(Constants.UrlCreateUser)}
            {...props}
        />
    );

    public closeResetTermsAcceptanceDialog = () => {
        this.setState({ showResetTermsAcceptanceDialog: false });
    };

    private showResetTermsAcceptanceDialog = () => {
        this.setState({ showResetTermsAcceptanceDialog: true });
    };
}

const reduxState = (state: IAppState) =>
    Object.assign({}, state.auctionStoreState, state.userStoreState);
const reduxDispatch = (dispatch: IAppDispatch) =>
    bindActionCreators(
        Object.assign(
            {},
            AuctionStore.actionCreators,
            UserStore.actionCreators
        ),
        dispatch
    );

export default withStyles(styles)(
    connect(reduxState, reduxDispatch)(ManageUserList)
);
